import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { deviceService } from '@/services/device-service';
import { toast } from 'sonner';
import { useUser } from '@/context/user-provider';
import { 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Trash2, 
  RotateCcw,
  Monitor
} from 'lucide-react';

interface DeviceWithFormattedData {
  id: string;
  deviceCode: string;
  userCode: string;
  clientId?: string;
  scope?: string;
  status: 'pending' | 'approved' | 'denied';
  createdAt: string;
  expiresAt: string;
  lastPolledAt?: string;
  formattedCreatedAt: string;
  formattedExpiresAt: string;
  statusDisplay: string;
  statusIcon: JSX.Element;
  statusColor: string;
}

export const DeviceManagementPage = () => {
  const [devices, setDevices] = useState<DeviceWithFormattedData[]>([]);
  const [loading, setLoading] = useState(true);
  const [revokingDeviceId, setRevokingDeviceId] = useState<string | null>(null);
  const { user } = useUser();

  // Fetch devices
  const fetchDevices = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const response = await deviceService.listDevices();
      
      // Format the device data for display
      const formattedDevices = response.devices.map(device => ({
        ...device,
        formattedCreatedAt: new Date(device.createdAt).toLocaleString(),
        formattedExpiresAt: new Date(device.expiresAt).toLocaleString(),
        statusDisplay: device.status.charAt(0).toUpperCase() + device.status.slice(1),
        statusIcon: device.status === 'approved' ? 
          <CheckCircle className="h-4 w-4 text-green-500" /> :
          device.status === 'pending' ? 
          <Clock className="h-4 w-4 text-yellow-500" /> :
          <AlertTriangle className="h-4 w-4 text-destructive" />,
        statusColor: device.status === 'approved' ? 
          'text-green-600 bg-green-50' :
          device.status === 'pending' ? 
          'text-yellow-600 bg-yellow-50' :
          'text-destructive bg-destructive/10',
      }));
      
      setDevices(formattedDevices);
    } catch (error) {
      console.error('Error fetching devices:', error);
      toast.error('Failed to load devices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Revoke a device
  const handleRevokeDevice = async (deviceId: string) => {
    if (!window.confirm('Are you sure you want to revoke access for this device?')) {
      return;
    }

    setRevokingDeviceId(deviceId);
    
    try {
      await deviceService.revokeDevice(deviceId);
      toast.success('Device access revoked successfully');
      // Refetch devices to update the list
      await fetchDevices();
    } catch (error) {
      console.error('Error revoking device:', error);
      toast.error('Failed to revoke device access. Please try again.');
    } finally {
      setRevokingDeviceId(null);
    }
  };

  // Refresh devices when component mounts
  useEffect(() => {
    if (user) {
      fetchDevices();
    }
  }, [user]);

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Please log in to view your devices.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Device Management</h1>
        <p className="text-muted-foreground mt-2">
          Manage devices that have access to your account
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Monitor className="h-5 w-5" />
            Authorized Devices
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            These are devices that have been granted access to your account.
            You can revoke access at any time.
          </p>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <p>Loading devices...</p>
            </div>
          ) : devices.length === 0 ? (
            <div className="text-center py-8">
              <Monitor className="h-12 w-12 mx-auto text-muted-foreground" />
              <h3 className="mt-4 text-lg font-medium">No devices found</h3>
              <p className="text-muted-foreground mt-1">
                No devices have been authorized to access your account yet.
              </p>
            </div>
          ) : (
            <ScrollArea className="h-[calc(100vh-300px)] max-h-[600px] w-full">
              <Table className="w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead>Device</TableHead>
                    <TableHead>Client ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices.map((device) => (
                    <TableRow key={device.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Monitor className="h-4 w-4 text-muted-foreground" />
                          <span>Device: {device.userCode.substring(0, 4)}...{device.userCode.substring(device.userCode.length - 4)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="truncate max-w-[150px]" title={device.clientId || 'N/A'}>
                          {device.clientId || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${device.statusColor}`}>
                          {device.statusIcon}
                          <span>{device.statusDisplay}</span>
                        </div>
                      </TableCell>
                      <TableCell title={device.formattedCreatedAt}>
                        <div className="text-sm text-muted-foreground">
                          {new Date(device.createdAt).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell title={device.formattedExpiresAt}>
                        <div className="text-sm text-muted-foreground">
                          {new Date(device.expiresAt).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRevokeDevice(device.id)}
                          disabled={revokingDeviceId === device.id}
                        >
                          {revokingDeviceId === device.id ? (
                            <>
                              <RotateCcw className="h-4 w-4 mr-2 animate-spin" />
                              Revoking...
                            </>
                          ) : (
                            <>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Revoke
                            </>
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};